/** Fictional radar signals. These shapes are game clues, not physical measurements. */
export const KINDS = ['aircraft', 'birds', 'weather', 'echo'] as const
export type Kind = typeof KINDS[number]
export const VIEWS = ['echo', 'spectrum', 'pulse'] as const
export type View = typeof VIEWS[number]
export type Band = 'A' | 'B'
export type Confidence = 'tentative' | 'likely' | 'certain'
export const SHIFT_SECONDS = 180
export const START_POWER = 12
export const POWER_REGEN_SECONDS = 10
export const REFERENCE: Record<Kind, { name: string; movement: string; signature: string }> = {
  aircraft: {
    name: 'Aircraft', movement: 'A steady track, sometimes with a gentle turn.',
    signature: 'A concentrated echo and narrow frequency band. Pulse strength stays fairly steady.',
  },
  birds: {
    name: 'Birds', movement: 'A wavering track that can still look quite direct.',
    signature: 'Several nearby echo peaks. A spread of frequencies and rhythmic changes in pulse strength.',
  },
  weather: {
    name: 'Weather', movement: 'Slow drift, with some local wandering.',
    signature: 'A broad echo and diffuse frequencies. Pulse strength changes gradually.',
  },
  echo: {
    name: 'Sensor echo', movement: 'May jump or hover, but can briefly mimic a real track.',
    signature: 'Separated sharp peaks and a narrow frequency spike. Pulse strength cuts in and out.',
  },
}
export const VIEW_LABELS: Record<View, string> = {
  echo: 'Echo shape', spectrum: 'Frequency spread', pulse: 'Pulse history',
}

export interface Contact {
  id: number
  kind: Kind
  arrives: number
  leaves: number
  variation: number
  phase: number
  angle: number
  band: Band
  focus: number
  report: { answer: Kind | null; confidence: Confidence; at: number; lost: boolean; points: number } | null
}
export interface Shift {
  seed: number
  elapsed: number
  power: number
  status: 'running' | 'paused' | 'finished'
  contacts: Contact[]
}

/** Local reproducible PRNG: gameplay only, never cryptographic randomness. */
function random(seed: number): () => number {
  let state = seed >>> 0
  return () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0
    return state / 4294967296
  }
}

export function createShift(seed: number): Shift {
  const draw = random(seed)
  const kinds: Kind[] = [...KINDS, ...KINDS, KINDS[Math.floor(draw() * KINDS.length)]!]
  for (let i = kinds.length - 1; i > 0; i--) {
    const j = Math.floor(draw() * (i + 1))
    ;[kinds[i], kinds[j]] = [kinds[j]!, kinds[i]!]
  }
  const arrivals = [0, 12, 28, 46, 64, 84, 106, 128, 148]
  return {
    seed: seed >>> 0, elapsed: 0, power: START_POWER, status: 'running',
    contacts: kinds.map((kind, i) => ({
      id: i + 1, kind, arrives: arrivals[i]!, leaves: Math.min(arrivals[i]! + 44, SHIFT_SECONDS),
      variation: draw() - 0.5, phase: draw() * Math.PI * 2, angle: draw() * Math.PI * 2,
      band: 'A', focus: 0, report: null,
    })),
  }
}

export function activeContacts(shift: Shift): Contact[] {
  return shift.contacts.filter(c => c.arrives <= shift.elapsed && shift.elapsed < c.leaves && !c.report)
}

function available(shift: Shift, id: number): Contact | undefined {
  if (shift.status !== 'running') return undefined
  return activeContacts(shift).find(c => c.id === id)
}

export function advance(shift: Shift, seconds: number): void {
  if (shift.status !== 'running' || !Number.isFinite(seconds) || seconds <= 0) return
  const previousRecharge = Math.floor(shift.elapsed / POWER_REGEN_SECONDS)
  shift.elapsed = Math.min(SHIFT_SECONDS, shift.elapsed + seconds)
  const recharge = Math.floor(shift.elapsed / POWER_REGEN_SECONDS) - previousRecharge
  shift.power = Math.min(START_POWER, shift.power + recharge)
  for (const c of shift.contacts) {
    if (!c.report && shift.elapsed >= c.leaves) {
      c.report = { answer: null, confidence: 'tentative', at: c.leaves, lost: true, points: -3 }
    }
  }
  if (shift.elapsed === SHIFT_SECONDS) shift.status = 'finished'
}

export function focusContact(shift: Shift, id: number): boolean {
  const c = available(shift, id)
  if (!c || shift.power < 2 || c.focus >= 2) return false
  c.focus++
  shift.power -= 2
  return true
}

export function changeBand(shift: Shift, id: number): boolean {
  const c = available(shift, id)
  if (!c || shift.power < 1) return false
  c.band = c.band === 'A' ? 'B' : 'A'
  shift.power--
  return true
}

export function fileReport(shift: Shift, id: number, answer: Kind | null, confidence: Confidence): boolean {
  const c = available(shift, id)
  if (!c) return false
  const rewards = { tentative: 4, likely: 7, certain: 10 }
  const penalties = { tentative: -2, likely: -6, certain: -14 }
  c.report = {
    answer, confidence, at: shift.elapsed, lost: false,
    points: answer === null ? 0 : answer === c.kind ? rewards[confidence] : penalties[confidence],
  }
  if (shift.contacts.every(contact => contact.report)) shift.status = 'finished'
  return true
}

export function finishShift(shift: Shift): void {
  if (shift.status === 'finished') return
  // Ending a shift explicitly files remaining contacts as unresolved, not guessed.
  for (const c of shift.contacts) {
    c.report ??= { answer: null, confidence: 'tentative', at: shift.elapsed, lost: false, points: 0 }
  }
  shift.status = 'finished'
}

const clamp = (x: number) => Math.max(0, Math.min(1, x))
const peak = (x: number, centre: number, width: number) => Math.exp(-0.5 * ((x - centre) / width) ** 2)

export function cleanProfile(kind: Kind, view: View, band: Band, variation = 0, phase = 0): number[] {
  return Array.from({ length: 64 }, (_, i) => {
    const x = i / 63
    const offset = variation * 0.12 + (band === 'B' ? 0.035 : 0)
    let y = 0
    if (view === 'echo') {
      if (kind === 'aircraft') y = 0.78 * peak(x, 0.48 + offset, 0.06) + 0.12 * peak(x, 0.64 + offset, 0.04)
      if (kind === 'birds') y = 0.46 * peak(x, 0.34 + offset, 0.05) + 0.65 * peak(x, 0.51 + offset, 0.05) + 0.38 * peak(x, 0.67 + offset, 0.06)
      if (kind === 'weather') y = 0.62 * peak(x, 0.5 + offset, 0.22 + variation * 0.04)
      if (kind === 'echo') y = 0.8 * peak(x, 0.26 + offset, 0.025) + (band === 'B' ? 0.28 : 0.7) * peak(x, 0.74 - offset, 0.025)
    } else if (view === 'spectrum') {
      if (kind === 'aircraft') y = 0.8 * peak(x, 0.59 + offset, 0.055)
      if (kind === 'birds') y = 0.4 * peak(x, 0.36 + offset, 0.07) + 0.55 * peak(x, 0.53 + offset, 0.08) + 0.35 * peak(x, 0.7 + offset, 0.06)
      if (kind === 'weather') y = 0.57 * peak(x, 0.49 + offset, 0.25)
      if (kind === 'echo') y = 0.84 * peak(x, 0.5, band === 'B' ? 0.045 : 0.025)
    } else {
      if (kind === 'aircraft') y = 0.65 + 0.04 * Math.sin(x * 12 + phase)
      if (kind === 'birds') y = 0.49 + (band === 'B' ? 0.31 : 0.23) * Math.sin(x * Math.PI * 8 + phase)
      if (kind === 'weather') y = 0.47 + 0.19 * Math.sin(x * 5 + phase)
      if (kind === 'echo') y = 0.08 + 0.76 * peak(x, 0.22 + offset, 0.035) + 0.64 * peak(x, 0.69 - offset, 0.045)
    }
    return clamp(0.04 + y * (1 + variation * 0.18))
  })
}

export function observe(shift: Shift, c: Contact, view: View): number[] {
  const age = Math.max(0, shift.elapsed - c.arrives)
  const draw = random(shift.seed ^ Math.imul(c.id, 2654435761) ^ Math.floor(age / 4) ^ (c.band === 'A' ? 81 : 193) ^ Math.imul(VIEWS.indexOf(view) + 1, 997))
  const noise = [0.3, 0.15, 0.065][c.focus]!
  return cleanProfile(c.kind, view, c.band, c.variation, c.phase)
    .map(y => clamp(y + (draw() - 0.5) * noise * 2))
}

/** Normalized scope coordinates, with observed jitter, never the hidden truth label. */
export function position(c: Contact, age: number): { x: number; y: number } {
  const t = Math.max(0, Math.min(44, age))
  const speed = c.kind === 'weather' ? 0.006 : 0.026
  let along = -0.57 + t * speed
  let across = 0.22 * Math.sin(c.phase)
  if (c.kind === 'birds') across += 0.075 * Math.sin(t * 0.55 + c.phase)
  if (c.kind === 'weather') across += 0.035 * Math.sin(t * 0.19 + c.phase)
  if (c.kind === 'echo') {
    along += 0.16 * Math.sin(Math.floor(t / 6) * 2 + c.phase)
    across += 0.13 * Math.cos(Math.floor(t / 5) * 3 + c.phase)
  }
  across += 0.008 * Math.sin(t * 2 + c.phase)
  return { x: 0.5 + 0.65 * (along * Math.cos(c.angle) - across * Math.sin(c.angle)), y: 0.5 + 0.65 * (along * Math.sin(c.angle) + across * Math.cos(c.angle)) }
}

export function profileDescription(values: number[]): string {
  const max = Math.max(...values)
  const occupied = values.filter(y => y > max * 0.5).length / values.length
  const variation = values.slice(1).reduce((sum, y, i) => sum + Math.abs(y - values[i]!), 0) / (values.length - 1)
  return `Peak ${Math.round(max * 100)}%. ${Math.round(occupied * 100)}% of samples above half peak. ${variation > 0.12 ? 'Rapid' : variation > 0.04 ? 'Moderate' : 'Small'} changes between samples.`
}
